import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CreditCard, Loader2, LogIn, UserPlus } from "lucide-react";

interface PaymentStepProps {
  applicationId: string | null;
  formData: any;
  onBack: () => void;
  onComplete: () => void;
  onSaveApplication: () => Promise<string | null>;
}

export const PaymentStep = ({ applicationId, formData, onBack, onComplete, onSaveApplication }: PaymentStepProps) => {
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isSavingApplication, setIsSavingApplication] = useState(false);
  const [email, setEmail] = useState(formData?.email || "");
  const [password, setPassword] = useState("");
  const [savedApplicationId, setSavedApplicationId] = useState(applicationId);
  const { toast } = useToast();

  useEffect(() => {
    checkAuthentication();
    
    // Listen for auth state changes (synchronous only to avoid deadlocks)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const checkAuthentication = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const authenticated = !!session;
    setIsAuthenticated(authenticated);
    setCheckingAuth(false);
    
    // If already authenticated and no saved application, save it now
    if (authenticated && !applicationId && !savedApplicationId) {
      await saveApplicationAfterAuth();
    }
  };

  const saveApplicationAfterAuth = async () => {
    if (isSavingApplication) {
      console.log('[PaymentStep] Already saving, skipping');
      return;
    }
    
    console.log('[PaymentStep] Starting saveApplicationAfterAuth');
    setIsSavingApplication(true);
    try {
      toast({
        title: "Saving application",
        description: "Setting up your profile...",
      });

      // Wait 3 seconds for database trigger to complete profile creation
      console.log('[PaymentStep] Waiting for profile creation...');
      await new Promise(resolve => setTimeout(resolve, 3000));

      console.log('[PaymentStep] Calling onSaveApplication');
      const savedId = await onSaveApplication();
      console.log('[PaymentStep] Save result:', savedId);
      
      if (savedId) {
        setSavedApplicationId(savedId);
        toast({
          title: "Application saved",
          description: "You can now proceed to payment.",
        });
      } else {
        throw new Error("Failed to save application");
      }
    } catch (error: any) {
      console.error('[PaymentStep] Error saving application:', error);
      toast({
        title: "Error saving application",
        description: error.message || "Please wait a moment and try again.",
        variant: "destructive",
      });
      setIsProcessing(false);
    } finally {
      setIsSavingApplication(false);
    }
  };

  const handleSignUp = async () => {
    if (!email || !password) {
      toast({
        title: "Missing information",
        description: "Please provide email and password",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
          }
        }
      });

      if (error) throw error;

      // Check if email confirmation is required
      if (data?.user && !data.session) {
        toast({
          title: "Confirm your email",
          description: "Please check your email and click the confirmation link to continue.",
        });
        setIsProcessing(false);
        return;
      }

      // Account created successfully, now save the application
      setIsAuthenticated(true);
      await saveApplicationAfterAuth();
      
    } catch (error: any) {
      toast({
        title: "Signup failed",
        description: error.message,
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  const handleSignIn = async () => {
    if (!email || !password) {
      toast({
        title: "Missing information",
        description: "Please provide email and password",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Signed in successfully, now save the application
      setIsAuthenticated(true);
      await saveApplicationAfterAuth();
      
    } catch (error: any) {
      toast({
        title: "Sign in failed",
        description: error.message,
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  const handlePayment = async () => {
    console.log('[PaymentStep] handlePayment called');
    console.log('[PaymentStep] agreedToTerms:', agreedToTerms);
    console.log('[PaymentStep] savedApplicationId:', savedApplicationId);
    
    if (!agreedToTerms) {
      console.log('[PaymentStep] Terms not agreed to');
      toast({
        title: "Terms required",
        description: "Please agree to the terms and conditions to continue",
        variant: "destructive",
      });
      return;
    }

    if (!savedApplicationId) {
      console.log('[PaymentStep] No saved application ID');
      toast({
        title: "Error",
        description: "Application not saved. Please try again.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    console.log('[PaymentStep] Calling create-application-checkout function');

    try {
      const { data, error } = await supabase.functions.invoke('create-application-checkout', {
        body: { applicationId: savedApplicationId },
      });

      console.log('[PaymentStep] Function response:', { data, error });

      if (error) {
        console.error('[PaymentStep] Function error:', error);
        throw error;
      }

      if (data?.url) {
        console.log('[PaymentStep] Checkout URL received:', data.url);
        window.open(data.url, '_blank');
        
        toast({
          title: "Redirecting to payment",
          description: "Complete your payment in the new window",
        });

        console.log('[PaymentStep] Completing application flow');
        setTimeout(() => {
          onComplete();
        }, 2000);
      } else {
        console.error('[PaymentStep] No URL in response:', data);
        throw new Error('No payment URL received');
      }
    } catch (error: any) {
      console.error('[PaymentStep] Payment error:', error);
      console.error('[PaymentStep] Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      
      toast({
        title: "Payment error",
        description: error.message || "Failed to initiate payment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      console.log('[PaymentStep] Processing complete');
    }
  };

  if (checkingAuth || isSavingApplication) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="text-sm text-muted-foreground">
          {checkingAuth ? "Checking authentication..." : "Saving your application..."}
        </p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold">Create Account or Sign In</h3>
          <p className="text-sm text-muted-foreground mt-2">
            To complete your application and proceed with payment, please create an account or sign in.
          </p>
        </div>

        <Tabs defaultValue="signup" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="signup">Create Account</TabsTrigger>
            <TabsTrigger value="signin">Sign In</TabsTrigger>
          </TabsList>

          <TabsContent value="signup" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="signup-email">Email</Label>
              <Input
                id="signup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signup-password">Password</Label>
              <Input
                id="signup-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Choose a secure password"
              />
            </div>
            <Button 
              className="w-full" 
              onClick={handleSignUp}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Create Account & Continue
                </>
              )}
            </Button>
          </TabsContent>

          <TabsContent value="signin" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="signin-email">Email</Label>
              <Input
                id="signin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="signin-password">Password</Label>
              <Input
                id="signin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
              />
            </div>
            <Button 
              className="w-full" 
              onClick={handleSignIn}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign In & Continue
                </>
              )}
            </Button>
          </TabsContent>
        </Tabs>

        <div className="flex justify-between pt-4 border-t">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">Application Fee Payment</h3>
      
      <div className="bg-muted p-6 rounded-lg space-y-4">
        <div className="flex justify-between items-center">
          <span className="text-lg font-medium">Application Fee</span>
          <span className="text-2xl font-bold">$50.00</span>
        </div>
        <p className="text-sm text-muted-foreground">
          This is a non-refundable fee required to process your application to Harvest Time International Christian University.
        </p>
      </div>

      <div className="space-y-4">
        <h4 className="font-medium">What's included:</h4>
        <ul className="list-disc list-inside space-y-2 text-sm text-muted-foreground">
          <li>Application review by admissions committee</li>
          <li>Academic credential evaluation</li>
          <li>Interview scheduling (if required)</li>
          <li>Access to admissions counselor</li>
        </ul>
      </div>

      <div className="flex items-start space-x-3 space-y-0 rounded-md border p-4">
        <Checkbox
          id="terms"
          checked={agreedToTerms}
          onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
        />
        <div className="space-y-1 leading-none">
          <label
            htmlFor="terms"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            I agree to the terms and conditions
          </label>
          <p className="text-sm text-muted-foreground">
            I understand that the $50 application fee is non-refundable and I have provided accurate information in my application.
          </p>
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack} disabled={isProcessing}>
          Back
        </Button>
        <Button onClick={handlePayment} disabled={!agreedToTerms || isProcessing}>
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <CreditCard className="mr-2 h-4 w-4" />
              Pay $50 & Submit
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
